import { makeObservable, makeAutoObservable, computed, action } from 'mobx';
import { ArgumentError } from '../utils/errors';
import { BaseAPIModel, fetcher } from '@weezevent/weezjs-core';
import RateTemplate from './rateTemplate';

export const InputMaxPrice = 10000;

/**
 * Rate Proxy handles the retrieval of properties, including those prefixed with 'resolved_'.
 * It specifically addresses the case of `contingents` and similar properties that might
 * be either directly available or nested within `rate_template`.
 *
 * Key Logic:
 * 1. If the property is prefixed with 'resolved_', we:
 *    - Remove the 'resolved_' prefix and check if the property exists in the current object.
 *    - If the property exists and is a non-null object, we check if it's an array:
 *      - If it's a non-empty array, we return it.
 *      - If it's an empty array, we fallback to the `rate_template` version of the property (if available).
 *    - If the property is not an array, we return its direct value.
 * 2. If the value is `null`, we fallback to the `rate_template` value for that property.
 *
 * Special Considerations:
 * - An empty array (`[]`) is treated as an object in JavaScript, not `null`, which is why the explicit check for length is necessary.
 * - Categories with `null` values are specifically handled by retrieving them from `rate_template`.
 */
const RateProxy = {
    get: (obj, props) => {
        if (!(typeof props === 'string')) {
            return obj[props];
        }
        if (/^resolved_/.test(props)) {
            const property = props.replace('resolved_', '');
            const value = obj[property];
            if (value !== null) {
                if (typeof value === 'object' && obj.rate_template !== null) {
                    return value.length ? value : obj.rate_template?.[property];
                } else {
                    return value;
                }
            }
            return obj.rate_template?.[property];
        }
        return obj[props];
    }
};

export default class RateModel extends BaseAPIModel {
    constructor(...args) {
        let res = super(...args);

        return new Proxy(res, RateProxy);
    }

    fields() {
        return {
            id: { readOnly: true, primaryKey: true }
        };
    }

    toAPI() {
        let result = super.toAPI();
        delete result['distributor'];

        return result;
    }

    toSeatlib() {
        return {
            id: this.pk,
            name: this.name,
            price: this.price || 0
        };
    }

    patch({ organizationId, eventId }, changes) {
        // saves only the changes you put in "changes" to the api
        const url = this.constructor.getItemUrl({ organizationId, eventId, id: this.id });
        return fetcher.patch(url, { data: changes });
    }

    static fromJSON(rate) {
        let newRate = super.fromJSON(rate);
        if (newRate.rate_template) {
            newRate.rate_template = RateTemplate.fromJSON(newRate.rate_template);
        }
        // newRate.setQuantity(data.preselected_quantity ?? 1);
        return newRate;
    }

    static listUrl({ organizationId, eventId }) {
        if (!organizationId || !eventId) {
            throw new ArgumentError(`[Model][RateModel][getAll] organizationId and eventId are mandatory`);
        }

        return `/ticket/organizations/${organizationId}/events/${eventId}/rates`;
    }

    // Get rate's properties related to rate templates, wether linked or unlinked to a specific event.
    getName() {
        return this.rate_template && !this.name ? this.rate_template.name : this.name;
    }

    getPrice() {
        return this.rate_template && !this.price ? this.rate_template.price : this.price;
    }

    getCategory() {
        return this.rate_template && !this.category ? this.rate_template.category : this.category;
    }

    // Get the select options object for this rate
    toOption(custom) {
        return {
            text: this.rate_template?.name ?? this.name,
            value: this.id,
            ...custom?.(this)
        };
    }

    // Usage on demand attributes

    makeObservable() {
        if (!this._observable) {
            this.setQuantity(this.preselected_quantity || 1);
            this.setUnitPrice(this.price);
            this.setQuotas({ event: null, group: null, rate: null });
            this.getQuota();
            makeAutoObservable(this.object);
            makeObservable(this, {
                quantity: computed,
                setQuantity: action,
                unitPrice: computed,
                setUnitPrice: action,
                totalAmount: computed,
                setTotalAmount: action
            });
            this._observable = true;
        }
    }

    get quantity() {
        return this.object.quantity;
    }

    get unitPrice() {
        return this.object.unitPrice;
    }

    get maxQuantity() {
        return parseInt(this.cartMaxQuantity) || InputMaxPrice; // To fit with Nacre@ Counter max
    }

    get minQuantity() {
        return parseInt(this.cartMinQuantity) || 1;
    }
    get feesAmount() {
        return this.fees?.reduce?.((acc, fee) => fee?.amount ?? 0 + acc, 0) ?? 0;
    }

    get totalAmount() {
        return parseFloat(this.quantity * this.unitPrice + this.feesAmount).toFixed(2);
    }

    setQuantity(value) {
        let previousQuantity = this.quantity;
        this.quantity = Math.max(this.minQuantity, parseInt(value) || 1);
        if (this.maxQuantity) {
            this.quantity = Math.min(this.quantity, this.maxQuantity);
        }
        this.decrementQuotas(previousQuantity, this.quantity);
    }

    setUnitPrice(value) {
        this.unitPrice = (parseFloat(value) || 0)?.toFixed(2);
    }

    setTotalAmount(value) {
        this.setUnitPrice(value / this.quantity);
    }

    /* Quotas */
    getQuota() {
        let list_quotas = [];
        if (this.quotas_details.rate) {
            list_quotas.push(this.quotas_details.rate);
        }
        if (this.quotas_details.group) {
            list_quotas.push(this.quotas_details.group);
        }
        if (this.quotas_details.event) {
            list_quotas.push(this.quotas_details.event);
        }

        let quota = Math.min(...list_quotas);
        return quota + this.quantity;
    }
    setQuotas(value) {
        this.quotas_details = value;
    }
    decrementQuotas(previousQuantity, nextQuantity) {
        let quantityToDecrement = nextQuantity - previousQuantity;
        let rateList = [];
        if (this.quotas_details && this.quotas_details.event) {
            // Modify quotas on all rates
            rateList = this.parent?.values;
        }

        if (this.quotas_details && !this.quotas_details.event && this.quotas_details.group) {
            // Modify quotas on rate in group
            // TODO : do not work for sub_group :(
            rateList = this.parent?.values.filter(rate => rate.group === this.group);
        }

        if (this.quotas_details && !this.quotas_details.event && !this.quotas_details.group && this.quotas_details.rate) {
            // Modify quota on this rate
            rateList = [this];
        }

        rateList?.forEach?.(rate => {
            rate.setQuotas({
                event: rate.quotas_details.event !== null ? rate.quotas_details.event - quantityToDecrement : null,
                group: rate.quotas_details.group !== null ? rate.quotas_details.group - quantityToDecrement : null,
                rate: rate.quotas_details.rate !== null ? rate.quotas_details.rate - quantityToDecrement : null
            });
        });
    }
}
